'use client';

import { useSession } from 'next-auth/react';
import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { trpc } from '@/app/_trpc/client';
import { AccountType } from '@/autogenerated/@prisma/prisma-mongo-client';
import { AccountTypeBadge } from '@/components/features/admin/AccountTypeBadge';
import Avatar from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { BanStatus } from '@/constants/banStatus';

interface UserData {
  banned: boolean;
  didNotShowUp: boolean;
  testAccount: boolean;
  emailVerification: boolean;
  eligibilityVerified: boolean;
  eligibilityVerifiedBy: string;
  basicVerification: boolean;
  profileIsVisible: boolean;
  admin: boolean;
  teacher: boolean;
  legalName: string;
  idVerifiedBy: string;
  bannedReason?: string;
}

const defaultUserData: UserData = {
  banned: false,
  didNotShowUp: false,
  testAccount: false,
  emailVerification: false,
  eligibilityVerified: false,
  eligibilityVerifiedBy: '',
  basicVerification: false,
  profileIsVisible: true,
  admin: false,
  teacher: false,
  legalName: '',
  idVerifiedBy: '',
  bannedReason: '',
};

export default function AdminUserManagement({ userId }: { userId: string }) {
  const [userData, setUserData] = useState<UserData>(defaultUserData);
  const [legalName, setLegalName] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  const [isRemovePhotoDialogOpen, setIsRemovePhotoDialogOpen] = useState(false);
  const utils = trpc.useUtils();
  const updateUser = trpc.admin.users.update.useMutation({
    onSuccess: async () => {
      await utils.users.getById.invalidate({ id: userId });
      await utils.admin.users.search.invalidate();
    },
  });
  const removeProfilePhotoMutation = trpc.admin.users.removePhoto.useMutation({
    onSuccess: async () => {
      await utils.users.getById.invalidate({ id: userId });
      await utils.admin.users.search.invalidate();
    },
  });
  const sendUpdateAccountTypeEmail =
    trpc.email.sendUpdateAccountTypeEmail.useMutation();
  const { data: user } = trpc.users.getById.useQuery({ id: userId });
  const { data: session } = useSession();

  useEffect(() => {
    if (user) {
      setUserData({
        banned: user.bannedStatus === BanStatus.Banned,
        didNotShowUp: user.didNotShowUp ?? false,
        testAccount: user.testAccount ?? false,
        emailVerification: user.emailVerified ?? false,
        eligibilityVerified: !!user.eligibilityVerifiedBy,
        eligibilityVerifiedBy: user.eligibilityVerifiedBy ?? '',
        basicVerification: user.idVerificationStatus ?? false,
        profileIsVisible: user.publicProfile ?? false,
        admin: user.admin ?? false,
        teacher: user.roles?.includes('Teacher') ?? false,
        legalName: user.legalName ?? '',
        idVerifiedBy: user.idVerifiedBy ?? '',
        bannedReason: user.bannedReason ?? '',
      });
      setLegalName(user.legalName ?? '');
    }
  }, [user]);

  async function updateUserStatus(field: string, value: boolean) {
    setIsUpdating(true);

    try {
      const updateData: {
        userId: string;
        banned?: boolean;
        didNotShowUp?: boolean;
        testAccount?: boolean;
        emailVerification?: boolean;
        eligibilityVerified?: boolean;
        basicVerification?: boolean;
        idVerifiedBy?: string;
        profileIsVisible?: boolean;
        admin?: boolean;
        teacher?: boolean;
        bannedReason?: string;
      } = {
        userId,
      };

      switch (field) {
        case 'banned':
          updateData.banned = value;
          if (!value) {
            updateData.bannedReason = '';
          }
          break;
        case 'didNotShowUp':
          updateData.didNotShowUp = value;
          break;
        case 'testAccount':
          updateData.testAccount = value;
          break;
        case 'emailVerification':
          updateData.emailVerification = value;
          break;
        case 'eligibilityVerification':
          updateData.eligibilityVerified = value;
          break;
        case 'basicVerification':
          updateData.basicVerification = value;
          if (value) {
            updateData.idVerifiedBy = session?.user.name ?? '';
          }
          break;
        case 'profileIsVisible':
          updateData.profileIsVisible = value;
          break;
        case 'admin':
          updateData.admin = value;
          break;
        case 'teacher':
          updateData.teacher = value;
          break;
      }

      await updateUser.mutateAsync(updateData);

      setUserData((prev) => ({
        ...prev,
        [field]: value,
        ...(field === 'banned' && !value ? { bannedReason: '' } : {}),
        ...(field === 'eligibilityVerification'
          ? {
              eligibilityVerified: value,
              eligibilityVerifiedBy: session?.user.name ?? '',
            }
          : {}),
        ...(value && field === 'basicVerification'
          ? { idVerifiedBy: session?.user.name ?? '' }
          : {}),
      }));

      toast.success(`${field} has been ${value ? 'enabled' : 'disabled'}.`);
    } catch (error) {
      toast.error('Failed to update user status. Error: ' + error);
    } finally {
      setIsUpdating(false);
    }
  }

  async function saveLegalName() {
    setIsUpdating(true);

    try {
      await updateUser.mutateAsync({
        userId,
        legalName,
      });

      setUserData((prev) => ({ ...prev, legalName }));
      toast.success('Legal name updated.');
    } catch (error) {
      toast.error('Failed to update legal name. Error: ' + error);
    } finally {
      setIsUpdating(false);
    }
  }
  async function saveBannedReason() {
    setIsUpdating(true);

    try {
      await updateUser.mutateAsync({
        userId,
        bannedReason: userData.bannedReason,
      });

      toast.success('Banned reason updated.');
    } catch (error) {
      toast.error('Failed to update banned reason. Error: ' + error);
    } finally {
      setIsUpdating(false);
    }
  }
  async function changeAccountType(newAccountType: AccountType) {
    setIsUpdating(true);

    try {
      await updateUser.mutateAsync({
        userId,
        accountType: newAccountType,
      });

      toast.success('Account type updated.');
      if (user?.email) {
        await sendUpdateAccountTypeEmail.mutateAsync({
          email: user.email,
          name: `${user.name} ${user.surname}`,
          newAccountType,
        });
        toast.success('Account type updated email sent.');
      }
    } catch (error) {
      toast.error('Failed to update account type. Error: ' + error);
    } finally {
      setIsUpdating(false);
    }
  }
  async function removeProfilePhoto(): Promise<void> {
    setIsUpdating(true);

    try {
      await removeProfilePhotoMutation.mutateAsync({
        userId,
      });

      toast.success('Profile photo removed.');
      setIsRemovePhotoDialogOpen(false);
    } catch (error) {
      toast.error('Failed to remove profile photo. Error: ' + error);
    } finally {
      setIsUpdating(false);
    }
  }

  const checkboxItems = [
    {
      id: 'banned',
      checked: userData.banned,
      label: 'Banned',
      color: 'red' as const,
    },
    {
      id: 'didNotShowUp',
      checked: userData.didNotShowUp,
      label: 'Did not show up',
      color: 'red' as const,
    },
    {
      id: 'testAccount',
      checked: userData.testAccount,
      label: 'Test account',
      color: 'yellow' as const,
    },
    {
      id: 'emailVerification',
      checked: userData.emailVerification,
      label: 'Email verification',
      color: 'green' as const,
    },
    {
      id: 'eligibilityVerification',
      checked: userData.eligibilityVerified,
      label: 'Eligibility verification',
      color: 'green' as const,
      verifiedBy: userData.eligibilityVerified
        ? userData.eligibilityVerifiedBy
        : undefined,
    },
    {
      id: 'basicVerification',
      checked: userData.basicVerification,
      label: 'Basic verification',
      color: 'green' as const,
      verifiedBy: userData.basicVerification
        ? userData.idVerifiedBy
        : undefined,
    },
    {
      id: 'profileIsVisible',
      checked: userData.profileIsVisible,
      label: 'Profile is visible',
      color: 'green' as const,
    },
    {
      id: 'admin',
      checked: userData.admin,
      label: 'Admin',
      color: 'green' as const,
    },
    {
      id: 'teacher',
      checked: userData.teacher,
      label: 'Teacher',
      color: 'green' as const,
    },
  ];

  return (
    <Card className='w-full'>
      <CardHeader>
        <CardTitle>User Management</CardTitle>
      </CardHeader>
      <CardContent className='space-y-6'>
        <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
          {checkboxItems.map((item) => (
            <CheckboxItem
              key={item.id}
              {...item}
              onCheckedChange={(checked) => updateUserStatus(item.id, checked)}
              disabled={isUpdating}
            />
          ))}
        </div>

        {userData.banned && (
          <div className='border-t pt-4'>
            <Label htmlFor='bannedReason' className='text-sm font-semibold'>
              Banned Reason
            </Label>
            <div className='mt-2 flex space-x-2'>
              <Textarea
                id='bannedReason'
                value={userData.bannedReason}
                onChange={(e) =>
                  setUserData((prev) => ({
                    ...prev,
                    bannedReason: e.target.value,
                  }))
                }
                placeholder='Enter reason for banning'
                disabled={isUpdating}
              />
              <Button
                onClick={saveBannedReason}
                disabled={isUpdating || !userData.bannedReason}
              >
                Save
              </Button>
            </div>
          </div>
        )}

        <div className='flex flex-col items-start gap-2'>
          <Label htmlFor='accountType' className='text-sm font-semibold'>
            Profile Photo
          </Label>
          <Avatar
            imageUrl={user?.profile.profilePhoto ?? undefined}
            className='h-16 w-16'
          />
          {user?.profile.profilePhoto && (
            <Button
              variant='outline'
              size='medium'
              onClick={() => setIsRemovePhotoDialogOpen(true)}
            >
              Remove profile photo
            </Button>
          )}
        </div>
        <div className='flex flex-col items-start gap-2'>
          <Label htmlFor='accountType' className='text-sm font-semibold'>
            Account Type
          </Label>
          <AccountTypeBadge accountType={user?.accountType} />
          {user?.accountType && (
            <Button
              variant='outline'
              size='medium'
              onClick={() =>
                changeAccountType(
                  user?.accountType === AccountType.Refugee
                    ? AccountType.Supporter
                    : AccountType.Refugee
                )
              }
            >
              Change account type to{' '}
              {user?.accountType === AccountType.Refugee
                ? AccountType.Supporter
                : AccountType.Refugee}
            </Button>
          )}
        </div>
        <div className='border-t pt-4'>
          <Label htmlFor='legalName' className='text-sm font-semibold'>
            Legal Name
          </Label>
          <div className='mt-2 flex space-x-2'>
            <Input
              id='legalName'
              value={legalName}
              onChange={(e) => setLegalName(e.target.value)}
              placeholder={`${user?.name} ${user?.surname}`}
              disabled={isUpdating}
            />
            <Button
              onClick={saveLegalName}
              disabled={isUpdating || legalName === userData.legalName}
            >
              Save
            </Button>
          </div>
        </div>
      </CardContent>

      {/* Confirmation Dialog for Profile Photo Removal */}
      <Dialog
        open={isRemovePhotoDialogOpen}
        onOpenChange={setIsRemovePhotoDialogOpen}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove Profile Photo</DialogTitle>
            <DialogDescription>
              Are you sure you want to remove this user&apos;s profile photo?
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant='outline'
              onClick={() => setIsRemovePhotoDialogOpen(false)}
              disabled={isUpdating}
            >
              Cancel
            </Button>
            <Button
              onClick={removeProfilePhoto}
              disabled={isUpdating}
              loading={isUpdating}
            >
              Remove Photo
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

interface CheckboxItemProps {
  id: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  disabled: boolean;
  label: string;
  color?: 'red' | 'yellow' | 'green';
  verifiedBy?: string;
}

function CheckboxItem({
  id,
  checked,
  onCheckedChange,
  disabled,
  label,
  color = 'green',
  verifiedBy,
}: CheckboxItemProps) {
  const colorClasses = {
    red: 'border-red-500 data-[state=checked]:border-red-500 data-[state=checked]:bg-red-500 text-red-500',
    yellow:
      'border-yellow-500 data-[state=checked]:border-yellow-500 data-[state=checked]:bg-yellow-500 text-yellow-500',
    green:
      'border-green-500 data-[state=checked]:border-green-500 data-[state=checked]:bg-green-500 text-green-500',
  };

  return (
    <div className='flex items-center space-x-2'>
      <Checkbox
        id={id}
        checked={checked}
        onCheckedChange={(checked) => onCheckedChange(checked as boolean)}
        disabled={disabled}
        className={colorClasses[color]}
      />
      <Label htmlFor={id} className={colorClasses[color]}>
        {label}
      </Label>
      {verifiedBy && (
        <span className='text-muted-foreground ml-2 text-sm'>
          Verified by: {verifiedBy}
        </span>
      )}
    </div>
  );
}
